<script setup>
import { cloneDeep, get, orderBy } from 'lodash-es';
import { computed, onMounted, reactive, watch } from 'vue';
import BiHandsontable from '~/bi/components/widgets/table-widget/bi-handsontable.vue';
import { useBiTableHelper } from '~/bi/components/widgets/table-widget/composables/bi-table-helper.composables.js';
import { useBiStore } from '~/bi/store/bi.store';
import useEmitter from '~/common/composables/useEmitter';
import { sleep } from '~/common/utils/common.utils';

const props = defineProps({
  id: {
    type: String,
    default: () => crypto.randomUUID(),
  },
  data: {
    type: Array,
    default: null,
  },
  widgetBuilderMetadata: {
    type: Object,
    default: () => ({}),
  },
  config: {
    type: Object,
    default: () => ({}),
  },
  isBuilder: {
    type: Boolean,
    default: true,
  },
  isPreview: {
    type: Boolean,
    default: true,
  },
  height: {
    type: Number,
    default: 0,
  },
  additionalConfig: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['selectionEnd']);
const emitter = useEmitter();
const bi_store = useBiStore();
const {
  getCellFormatting,
  generateNestedTableHeaders,
  generateTableGroupHeaders,
  generateHandsontableData,
  getTableColumns,
  formatNumber,
} = useBiTableHelper();

const state = reactive({
  loading: false,
  error: null,
  data: [],
  columns: [],

  table_columns: [],
  table_data: [],
  nested_headers: [],
  nested_rows: false,
  column_config: {},

  table_instance: null,

  changes_detected: false,
  loading_changes: false,
  force_update: 1, // Will be updated in the future to update settings
});

const chart_config = computed(() => {
  const config_data = props.config?.chart || {};
  const column_alias_mapping = bi_store.alias_to_column_mapping(props.config?.query?.stages);
  const {
    type,

    columns_map = {},
    column_groups = [],
    show_row_headers = false,

    pivot_rows = [],
    pivot_columns = [],
    pivot_values = [],
    pivot_column_configs = {},
    show_row_totals = false,
    show_column_totals = false,
    show_grand_totals = false,

    conditional_formatting = [],
  } = config_data;

  if (type === 'table') {
    return { type, column_alias_mapping, columns_map, column_groups, conditional_formatting, show_row_headers };
  }
  else if (type === 'pivot_table') {
    return { type, column_alias_mapping, pivot_rows, pivot_columns, pivot_values, pivot_column_configs, show_row_totals, show_grand_totals, show_column_totals, conditional_formatting };
  }
  return { ...(config_data || {}), column_alias_mapping };
});
const enable_row_headers = computed(() => {
  let enable_headers = props.additionalConfig?.bi_table_settings?.rowHeaders || false;
  if (chart_config.value?.type === 'table')
    enable_headers = chart_config.value?.show_row_headers || false;
  return enable_headers;
});
const table_height = computed(() => {
  if (props.height)
    return props.height;
  // Header and Footer is about 85 each total 190
  // Additional table padding 40
  const available_height = window.innerHeight - (props.additionalConfig?.offsetHeight || 220);
  return available_height;
});
const columns_data_map = computed(() => {
  return (props.widgetBuilderMetadata?.columns || []).reduce((acc, col) => {
    acc[col.name] = col;
    return acc;
  }, {});
});

// -------------------------------- Methods --------------------------------- //

async function setupPivotTable(reinitialize = true) {
  if (props.isPreview && reinitialize) {
    const new_columns = (props.widgetBuilderMetadata?.columns || []).map(col => col.name);
    const alias_map = bi_store.alias_to_column_mapping();
    const keys_map = new_columns.reduce((acc, curr) => {
      if (alias_map[curr]?.is_aggregation) {
        acc.value_keys.push(curr);
      }
      else {
        acc.non_value_keys.push(curr);
      }
      acc[curr] = curr;
      return acc;
    }, { value_keys: [], non_value_keys: [] });
    let non_value_keys = keys_map.non_value_keys;
    const pivot_columns = (chart_config.value.pivot_columns || []).filter(key => non_value_keys.includes(key));
    const pivot_rows = (chart_config.value.pivot_rows || []).filter(key => non_value_keys.includes(key));

    // --- Case 1: Both are completely empty (Auto-assign evenly) ---
    if (pivot_columns.length === 0 && pivot_rows.length === 0) {
      const half = Math.ceil(non_value_keys.length / 2);
      bi_store.widget_builder_config.chart.pivot_columns = non_value_keys.slice(0, half);
      bi_store.widget_builder_config.chart.pivot_rows = non_value_keys.slice(half);
    }
    // --- Case 2: Only Rows are pre-filled (Assign rest to Columns) ---
    else if (pivot_columns.length === 0) {
      non_value_keys = non_value_keys.filter(key => !pivot_rows.includes(key));
      bi_store.widget_builder_config.chart.pivot_columns = non_value_keys;
      bi_store.widget_builder_config.chart.pivot_rows = pivot_rows;
    }
    // --- Case 3: Only Columns are pre-filled (Assign rest to Rows) ---
    else if (pivot_rows.length === 0) {
      non_value_keys = non_value_keys.filter(key => !pivot_columns.includes(key));
      bi_store.widget_builder_config.chart.pivot_rows = non_value_keys;
      bi_store.widget_builder_config.chart.pivot_columns = pivot_columns;
    }
    // --- Case 4: Both are pre-filled (Append rest to Columns) ---
    else {
      non_value_keys = non_value_keys.filter(key => !pivot_columns.includes(key) && !pivot_rows.includes(key));
      bi_store.widget_builder_config.chart.pivot_columns = [...pivot_columns, ...non_value_keys];
      bi_store.widget_builder_config.chart.pivot_rows = pivot_rows;
    }

    const old_pivot_values = bi_store.widget_builder_config.chart?.pivot_values || [];
    const new_pivot_values = keys_map.value_keys.map(key => ({
      column: key,
      aggregation: old_pivot_values.find(val => val.column === key)?.aggregation || 'sum',
    }));
    bi_store.widget_builder_config.chart.pivot_values = new_pivot_values;
  }

  const { pivot_rows = [], pivot_columns = [], pivot_values = [] } = chart_config.value;

  const value_keys = pivot_values.map(val => val.column);

  const nested_headers = await generateNestedTableHeaders(
    state.data,
    pivot_columns,
    value_keys,
    pivot_rows,
    chart_config.value,
  );

  const leaf_columns = nested_headers.slice(-1)[0];
  state.table_columns = getTableColumns(leaf_columns, chart_config.value, columns_data_map.value);
  state.nested_headers = nested_headers;

  const nested_data = await generateHandsontableData(
    state.data,
    pivot_columns,
    pivot_values,
    pivot_rows,
    chart_config.value,
  );

  if (pivot_rows?.length)
    state.nested_rows = true;

  state.table_data = nested_data;
}

async function setupTable(reinitialize = true) {
  if (!props.isBuilder && props.isPreview && reinitialize) {
    const columns_map = state.columns.reduce((acc, col_key, index) => {
      const col_data = bi_store.widget_builder_config.chart.columns_map?.[col_key] || {};
      const default_order_index = 1000 * (index + 1);
      acc[col_key] = {
        key: col_key,
        width: get(col_data, 'width', null),
        visible: get(col_data, 'visible', true),
        order_index: get(col_data, 'order_index', default_order_index),
        label: get(col_data, 'label', col_key),
      };
      return acc;
    }, {});
    bi_store.widget_builder_config.chart.columns_map = cloneDeep(columns_map);
    bi_store.widget_builder_config.chart.column_groups = chart_config.value.column_groups.map((group) => {
      group.columns = group.columns.filter(col_key => columns_map[col_key]);
      return group;
    });
  }

  if (props.isBuilder) {
    const table_columns = orderBy(
      state.columns,
      col => get(chart_config.value?.columns_map || {}, [col, 'order_index'], 0),
    );
    state.table_columns = getTableColumns(table_columns, chart_config.value, columns_data_map.value);
  }
  else {
    const table_columns = orderBy(
      state.columns.filter(col =>
        get(chart_config.value?.columns_map || {}, [col, 'visible'], true),
      ),
      col => get(chart_config.value?.columns_map || {}, [col, 'order_index'], 0),
    );
    state.table_columns = getTableColumns(table_columns, chart_config.value, columns_data_map.value);
    state.nested_headers = generateTableGroupHeaders(chart_config.value);
  }

  state.table_data = state.data;
  if (props.isBuilder) {
    state.column_config = state.columns.reduce((acc, col) => {
      const column_details = bi_store.alias_to_column_mapping()[col];
      if (column_details?.is_aggregation && column_details?.is_last_stage) {
        acc[col] = { backgroundColor: '#FFFAEB' };
      }
      return acc;
    }, {});
  }
}

// Format numeric data before assigning (async batched processing)
async function formatDataAsync(data) {
  const batchSize = 1000;
  const formatted_data = [];

  for (let i = 0; i < data.length; i += batchSize) {
    const batch = data.slice(i, i + batchSize);
    const formatted_batch = batch.map((row) => {
      const formatted_row = { ...row };
      (props.widgetBuilderMetadata?.columns || []).forEach((col) => {
        if (['number', 'integer', 'float'].includes(col.type)) {
          const value = row[col.name];
          if (value !== null && value !== undefined) {
            formatted_row[col.name] = formatNumber(value);
          }
        }
      });
      return formatted_row;
    });
    formatted_data.push(...formatted_batch);

    if (i + batchSize < data.length) {
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }

  return formatted_data;
}

async function initializeTable(reinitialize = true) {
  state.data = await formatDataAsync(props.data || []);
  state.columns = (props.widgetBuilderMetadata?.columns || [])?.map(col => col.name);

  if (chart_config.value?.type === 'pivot_table') {
    await setupPivotTable(reinitialize);
  }
  else {
    await setupTable(reinitialize);
  }
  bi_store.table_widget_config_change_detector = false;
}

function handleColumnResize(col_widths_map) {
  const type = chart_config.value.type;
  if (type !== 'table' && type !== 'pivot_table')
    return;

  const config_key = type === 'table' ? 'columns_map' : 'pivot_column_configs';

  function mergeWidths(target) {
    Object.entries(col_widths_map).forEach(([col_key, width]) => {
      target[col_key] = { ...(target[col_key] || {}), width };
    });
  }

  if (!props.isPreview) {
    const widget = bi_store.current_grid_layout.find(w => w.uid === props.id);
    if (widget) {
      emitter.emit(`ignoreNextConfigChange_${props.id}`);
      const updated_config = cloneDeep(widget.config);
      if (!updated_config.chart[config_key])
        updated_config.chart[config_key] = {};
      mergeWidths(updated_config.chart[config_key]);
      bi_store.updateWidget(props.id, { config: updated_config });
    }
    return;
  }

  if (!bi_store.widget_builder_config.chart[config_key])
    bi_store.widget_builder_config.chart[config_key] = {};
  mergeWidths(bi_store.widget_builder_config.chart[config_key]);
}

function additionalTableSettings() {
  let settings = {};
  if (props.isPreview || props.isBuilder || props.additionalConfig?.enableVirtualization) {
    settings = {
      viewportRowRenderingOffset: 100,
      viewportColumnRenderingOffset: 30,
    };
  }
  if (chart_config.value?.type === 'pivot_table') {
    settings = {
      ...settings,
      pagination: false,
      dropdownMenu: false,
      fixedRowsBottom: chart_config.value?.show_grand_totals ? 1 : 0,
    };
  }
  if (props.additionalConfig?.bi_table_settings) {
    settings = {
      ...settings,
      ...props.additionalConfig.bi_table_settings,
    };
  }
  if (props.data.length <= 25) {
    settings.pagination = false;
  }
  return settings;
}

// Helper functions that use the composable with current context
function getCellFormattingWithContext(params) {
  return getCellFormatting({
    ...params,
    chart_config: chart_config.value,
    table_columns: state.table_columns,
    table_data: state.data,
    table_columns_map: state.table_columns.reduce((acc, col) => {
      acc[col.data] = col;
      return acc;
    }, {}),
  });
}

watch(() => table_height.value, () => {
  if (state.table_instance?.hotInstance) {
    state.table_instance.hotInstance.updateSettings({
      height: table_height.value,
    });
  }
});
function showLoader(val) {
  if (state.table_instance) {
    const loading = state.table_instance.hotInstance.getPlugin('loading');
    if (val)
      loading.show();
    else
      loading.hide();
  }
}

watch(() => (bi_store.fetching_widget_data === props.id) || (bi_store.fetching_widget_data === 'preview'), (val) => {
  showLoader(val);
});

watch(() => (bi_store.table_widget_config_change_detector), async (val) => {
  if (!props.isPreview || !val)
    return;
  initializeTable(false);
  showLoader(true);
  await sleep(1000);
  showLoader(false);
  state.force_update++;
}, { deep: true });
// -------------------------------- Lifecycle ------------------------------- //
onMounted(() => {
  initializeTable();
});
</script>

<template>
  <div class="h-[100%] w-full">
    <div
      v-if="state.changes_detected"
      class="flex items-center justify-center w-full h-full"
    >
      <!-- Popup card -->
      <div class="bg-white rounded-lg shadow-lg border border-gray-200 p-6 flex flex-col items-center min-w-[600px]">
        <HawkFeaturedIcon theme="light-circle-outline" size="md" color="primary" class="mb-4">
          <IconHawkInfoCircle />
        </HawkFeaturedIcon>

        <div class="text-sm font-semibold mb-1">
          {{ $t('Changes detected') }}
        </div>

        <div class="text-sm text-gray-600 mb-6">
          {{ $t('New changes is been detected. You can reload to view new changes.') }}
        </div>

        <HawkButton :loading="state.loading_changes">
          <IconHawkRefreshCwTwo />
          <span class="text-sm font-medium">
            {{ $t('Load Preview') }}
          </span>
        </HawkButton>
      </div>
    </div>
    <div v-else-if="!state.table_data?.length" class="h-full w-full">
      <hawk-loader v-if="bi_store.fetching_widget_data === 'preview'" />
      <div v-else class="h-full w-full flex flex-col justify-center items-center text-center">
        <IconIllustrationBiEmptyDataBuilder />
        <div class="text-sm font-semibold text-gray-900 mt-4 mb-1">
          {{ $t('No data to display') }}
        </div>
        <div v-if="props.isBuilder" class="text-sm font-normal text-gray-600">
          {{ $t('Choose the columns on the left side to build the query and see the results') }}
        </div>
      </div>
    </div>
    <BiHandsontable
      v-else
      :key="state.force_update"
      :bi-table-id="`bi-table-widget-${id}`"
      :height="table_height"
      :data="state.table_data"
      :columns="state.table_columns"
      :column-config="state.column_config"
      :nested-headers="state.nested_headers"
      :nested-rows="state.nested_rows"
      :additional-table-settings="additionalTableSettings()"
      :show-skeleton-loader="state.loading"
      :get-cell-formatting="getCellFormattingWithContext"
      :row-headers="enable_row_headers"
      class="h-full border"
      :enable-column-sorting="chart_config.type !== 'pivot_table'"
      @table-instance="state.table_instance = $event"
      @column-resize="handleColumnResize"
      @selection-end="emit('selectionEnd', $event)"
    />
  </div>
</template>
